import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useGuru } from '../context/GuruContext';
import { useMasterData } from '../context/MasterDataContext';
import { supabase, ensureSupabaseTokenFresh, getSupabaseToken } from '../lib/supabase';
import { diagStep, timeoutSignal } from '../utils/presensiDiagnostics';
import { getWibDateString, UdzurMasuk } from '../utils/presensiLogic';
import type { PresensiRecord } from './usePresensiPegawai';

/**
 * Hook khusus untuk MENYIMPAN presensi (insert masuk / update pulang).
 *
 * Dipisah dari `usePresensiPegawai` (yang fokus membaca data hari ini) supaya
 * komponen yang memicu submit (PortalGuruBottomNav) tidak ikut membawa seluruh
 * logika fetching & auto-close yang hanya dibutuhkan oleh Dashboard.
 *
 * Sifat operasional yang penting:
 * 1. Payload token Supabase dibawa OLEH client (client-idempotency) — dipakai
 *    sebagai idempotency key di Cloud Function `presensiRequest` supaya
 *    auto-retry di jaringan lambat (data seluler) TIDAK PERNAH membuat data dobel.
 * 2. Auto-retry dengan backoff singkat (1s → 2s) khusus kegagalan jaringan
 *    (TypeError/fetch failed) — bukan validasi unik 23505.
 * 3. Batas waktu total (totalTimeoutMs) supaya spinner tidak berputar selamanya
 *    di jaringan putus; error-nya bisa langsung dibedakan di UI.
 */
const RETRY_DELAY_MS = [1000, 2000] as const;
const DEFAULT_TOTAL_TIMEOUT_MS = 30000;
// Batas waktu SATU permintaan. Tanpa ini, permintaan yang "nyangkut" di data
// seluler (paket hilang, pindah menara) menunggu sampai HP menyerah — bisa
// puluhan detik. Dengan batas ini permintaan dibatalkan lalu diulang otomatis
// (aman dari data dobel).
const PER_REQUEST_TIMEOUT_MS = 10000;

// Di produksi, fungsi RPC `presensiRequest` bisa tidak tersedia (PGRST202).
// Kalau begitu, ingat selama 24 jam supaya presensi berikutnya langsung lewat
// jalur cadangan — tidak membuang 1 permintaan sia-sia setiap presensi.
const RPC_MISSING_KEY = 'presensi_rpc_missing_until';
const RPC_MISSING_TTL_MS = 24 * 60 * 60 * 1000;

const isRpcKnownMissing = (): boolean => {
    try {
        return Number(localStorage.getItem(RPC_MISSING_KEY) || 0) > Date.now();
    } catch {
        return false;
    }
};

const markRpcMissing = (): void => {
    try { localStorage.setItem(RPC_MISSING_KEY, String(Date.now() + RPC_MISSING_TTL_MS)); } catch { /* noop */ }
};

const isRpcMissingError = (err: { code?: string; message?: string }): boolean => {
    const rawMsg = err.message || '';
    return err.code === 'PGRST202'
        || rawMsg.includes('Could not find the function')
        || (rawMsg.includes('presensiRequest') && rawMsg.includes('schema cache'));
};

const AFWAN_DUPLICATE_MESSAGE = "Afwan Presensi Gagal, kamu sudah bekerja dengan baik di shift yg kamu pilih di hari ini, kita bertemu di shift selanjutnya, semoga perkerjaanmu membawa berkah , jazakumullahu khairan";

const isNetworkError = (err: unknown): boolean => {
    if (err instanceof TypeError) return true;
    const msg = err instanceof Error ? err.message : String(err);
    return /fetch failed|networkerror|failed to fetch|network request failed|load failed|ECONNRESET|ETIMEDOUT|abort/i.test(msg);
};

const isAbortError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message : String(err);
    return /abort/i.test(msg);
};

export const usePresensiSubmit = (onSubmitted?: () => void) => {
    const { authUser } = useAuth();
    const { guruRecords } = useGuru();
    const { tahunAjaranRecords } = useMasterData();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const onSubmittedRef = useRef(onSubmitted);
    useEffect(() => {
        onSubmittedRef.current = onSubmitted;
    });

    const currentGuru = guruRecords.find(g => g.email === authUser?.email);
    const userName = currentGuru?.nama || authUser?.displayName || authUser?.email || 'Unknown';
    const userId = authUser?.uid;

    const getAcademicInfo = useCallback(() => {
        const activeTA = tahunAjaranRecords.find(t => t.status === 'Aktif');
        let tahunAjaran = '';
        if (activeTA) {
            const tMulai = activeTA.tanggalMulai instanceof Date ? activeTA.tanggalMulai : (activeTA.tanggalMulai as any)?.toDate?.() || new Date(activeTA.tanggalMulai);
            const tAkhir = activeTA.tanggalAkhir instanceof Date ? activeTA.tanggalAkhir : (activeTA.tanggalAkhir as any)?.toDate?.() || new Date(activeTA.tanggalAkhir);
            tahunAjaran = (activeTA as any).tahun || `${tMulai.getFullYear()}/${tAkhir.getFullYear()}`;
        } else {
            const now = new Date();
            const curYear = now.getFullYear();
            const curMonth = now.getMonth();
            tahunAjaran = curMonth >= 6 ? `${curYear}/${curYear + 1}` : `${curYear - 1}/${curYear}`;
        }
        const month = new Date().getMonth();
        const semester = month >= 6 ? 'Ganjil' : 'Genap';
        return { tahunAjaran, semester };
    }, [tahunAjaranRecords]);

    const submitAttendance = useCallback(async (
        type: 'masuk' | 'pulang',
        shiftName: string,
        locationName: string,
        status: 'tepat_waktu' | 'terlambat' | 'pulang_cepat' | 'lembur' | 'tidak_presensi_pulang',
        reason: string | null,
        coords: { lat: number; lng: number },
        distance: number,
        activeSession: PresensiRecord | null,
        totalTimeoutMs: number = DEFAULT_TOTAL_TIMEOUT_MS,
        /** Udzur masa penyesuaian: jam masuk dicatat = jam akhir toleransi, jam asli disimpan terpisah. */
        udzurMasuk?: { udzur: UdzurMasuk; jamMasuk: Date } | null
    ): Promise<{ success: boolean; message?: string }> => {
        if (!userId) return { success: false, message: 'User tidak ditemukan.' };
        if (type === 'pulang' && !activeSession) return { success: false, message: 'Tidak ada sesi aktif untuk dipulangkan.' };

        // Mulai penyegaran token Supabase di LATAR BELAKANG (non-blocking).
        // Kalau token hampir expired, refresh sudah berjalan saat RPC presensi
        // di bawah dipanggil — request Supabase akan menunggu refresh yang sama
        // via in-flight dedup di getSupabaseToken, jadi tidak ada AUTH_TOKEN_MISSING
        // di momen menekan tombol presensi (kasus umum di data seluler lemah).
        ensureSupabaseTokenFresh().catch(() => null);

        setSubmitting(true);
        setError(null);
        try {
            const now = new Date();
            const timestamp = now.toISOString();
            // Tanggal "hari presensi" dihitung dalam zona WIB — bukan jam HP —
            // supaya HP yang jamnya keliru tidak menulis/mencari tanggal yang salah.
            const tanggal = getWibDateString(now);
            const coordString = `${coords.lat},${coords.lng}`;
            const deadline = Date.now() + totalTimeoutMs;

            // Batas waktu SATU permintaan: tidak lebih dari PER_REQUEST_TIMEOUT_MS
            // dan tidak melewati batas waktu total.
            const requestSignal = () => timeoutSignal(Math.max(1000, Math.min(PER_REQUEST_TIMEOUT_MS, deadline - Date.now())));

            /** Cari record masuk untuk shift ini hari ini (dipakai cek dobel & verifikasi retry). */
            const findTodayRecord = async (): Promise<{ id: string } | null> => {
                const { data: existing, error: findError } = await supabase
                    .from('presensi_pegawai')
                    .select('id')
                    .eq('user_id', userId)
                    .eq('shift_masuk', shiftName)
                    .eq('tanggal', tanggal)
                    .abortSignal(requestSignal())
                    .maybeSingle();
                // Error (mis. timeout) TIDAK boleh dianggap "belum ada" → lempar
                // supaya loop retry yang menangani.
                if (findError) throw findError;
                return existing as { id: string } | null;
            };

            const runSubmit = async (clientRequestId: string, attempt: number) => {
                // Langkah 1: kunci akses Supabase (diukur terpisah untuk diagnosis).
                // Biasanya instan (tersimpan di HP); hanya lambat bila harus
                // meminta token baru ke Cloud Function.
                await diagStep('Token', () => getSupabaseToken());

                if (type === 'masuk') {
                    const { tahunAjaran, semester } = getAcademicInfo();
                    const payload = {
                        user_id: userId,
                        nama: userName,
                        tahun_ajaran: tahunAjaran,
                        semester,
                        tanggal,
                        shift_masuk: shiftName,
                        jam_masuk: udzurMasuk ? udzurMasuk.jamMasuk.toISOString() : timestamp,
                        lokasi_masuk: locationName,
                        koordinat_masuk: coordString,
                        status_masuk: status,
                        alasan_masuk: reason,
                        jarak_masuk: distance,
                        guru_id: currentGuru?.id || null,
                        email: currentGuru?.email || authUser?.email || null,
                        divisi: currentGuru?.divisi || null,
                        ...(udzurMasuk ? { udzur_masuk: udzurMasuk.udzur, jam_masuk_asli: timestamp } : {}),
                    };

                    // Jalur cadangan: insert langsung ke tabel (bila RPC tidak tersedia).
                    const directInsert = async () => {
                        const existing = await diagStep('Cek data', findTodayRecord);
                        if (existing) {
                            // Percobaan ulang setelah koneksi putus: percobaan
                            // SEBELUMNYA kemungkinan sudah tersimpan → anggap sukses.
                            if (attempt > 0) return;
                            try { window.dispatchEvent(new Event('presensi_updated')); } catch { /* noop */ }
                            throw new Error(AFWAN_DUPLICATE_MESSAGE);
                        }

                        const { error: insertError } = await diagStep('Simpan', async () => supabase
                            .from('presensi_pegawai')
                            .insert(payload)
                            .abortSignal(requestSignal()));

                        if (insertError) {
                            if (insertError.code === '23505') {
                                throw new Error(AFWAN_DUPLICATE_MESSAGE);
                            }
                            throw insertError;
                        }
                    };

                    if (isRpcKnownMissing()) {
                        await directInsert();
                        return;
                    }

                    const { data, error: supabaseError } = await diagStep('Simpan (RPC)', async () => supabase
                        .rpc('presensiRequest', {
                            p_payload: payload,
                            p_record_id: null,
                            p_client_request_id: clientRequestId,
                        })
                        .abortSignal(requestSignal()));

                    if (supabaseError) {
                        const rawMsg = supabaseError.message || '';
                        if (isRpcMissingError(supabaseError)) {
                            // Ingat supaya presensi berikutnya langsung lewat jalur
                            // cadangan (hemat 1 permintaan sia-sia).
                            markRpcMissing();
                            await directInsert();
                            return;
                        }

                        const isDuplicateSignal = supabaseError.code === '23505' || rawMsg.includes('sudah presensi') || rawMsg.includes('client_request_id');

                        if (!isDuplicateSignal) {
                            // Bukan sinyal duplikat → teruskan error server apa adanya.
                            throw supabaseError;
                        }

                        // Sinyal duplikat — VERIFIKASI ke server sebelum bicara:
                        const existing = await diagStep('Cek data', findTodayRecord);
                        if (existing) {
                            if (attempt > 0) return; // tersimpan oleh percobaan sebelumnya
                            try { window.dispatchEvent(new Event('presensi_updated')); } catch { /* noop */ }
                            throw new Error(AFWAN_DUPLICATE_MESSAGE);
                        }
                        throw supabaseError;
                    }
                    if (data === false) {
                        throw new Error(AFWAN_DUPLICATE_MESSAGE);
                    }
                    return;
                }

                const masukTime = new Date(activeSession!.jam_masuk).getTime();
                const pulangTime = new Date(timestamp).getTime();
                const durationSeconds = Math.floor((pulangTime - masukTime) / 1000);

                const payload = {
                    jam_pulang: timestamp,
                    lokasi_pulang: locationName,
                    koordinat_pulang: coordString,
                    status_pulang: status,
                    alasan_pulang: reason,
                    jarak_pulang: distance,
                    durasi_detik: durationSeconds,
                    updated_at: timestamp,
                };

                // Jalur cadangan: update langsung. Guard `jam_pulang is null`
                // membuat percobaan ulang aman (tidak menimpa pulang yang sudah tercatat).
                const directUpdate = async () => {
                    const { error: updateError } = await diagStep('Simpan', async () => supabase
                        .from('presensi_pegawai')
                        .update(payload)
                        .eq('id', activeSession!.id)
                        .is('jam_pulang', null)
                        .abortSignal(requestSignal()));
                    if (updateError) throw updateError;
                };

                if (isRpcKnownMissing()) {
                    await directUpdate();
                    return;
                }

                const { data, error: supabaseError } = await diagStep('Simpan (RPC)', async () => supabase
                    .rpc('presensiRequest', {
                        p_payload: payload,
                        p_record_id: activeSession!.id,
                        p_client_request_id: clientRequestId,
                    })
                    .abortSignal(requestSignal()));

                if (supabaseError) {
                    if (isRpcMissingError(supabaseError)) {
                        markRpcMissing();
                        await directUpdate();
                        return;
                    }
                    throw supabaseError;
                }
                if (data === false) throw new Error('Presensi pulang sudah tersimpan sebelumnya.');
            };

            // ID unik per percobaan submit (idempotency key)
            const clientRequestId = crypto.randomUUID();
            let attempt = 0;

            while (true) {
                try {
                    await runSubmit(clientRequestId, attempt);
                    onSubmittedRef.current?.();
                    return { success: true };
                } catch (err: any) {
                    // Token gagal (Cloud Function tak terjangkau) juga diulang selama masih
                    // ada waktu: percobaan berikutnya mencoba ulang semua rute token.
                    const retryable = isNetworkError(err) || isAbortError(err) || err?.message === 'AUTH_TOKEN_MISSING';
                    const timeLeft = deadline - Date.now();
                    if (retryable && attempt < RETRY_DELAY_MS.length && timeLeft > 0) {
                        const delay = Math.min(RETRY_DELAY_MS[attempt], timeLeft);
                        await new Promise(res => setTimeout(res, delay));
                        attempt++;
                        continue;
                    }

                    const message = err?.message
                        || 'Gagal menyimpan presensi. Silakan coba lagi.';
                    const errorMessage =
                        message === 'AUTH_TOKEN_MISSING'
                            ? "Gagal memverifikasi akses keamanan. Token tidak valid atau koneksi terputus. Silakan periksa koneksi internet Anda atau coba muat ulang halaman."
                            : (retryable || /fetch/i.test(message))
                                ? "Koneksi internet sedang lambat atau terputus, presensi BELUM tersimpan. Silakan coba lagi (bila bisa, pindah ke sinyal yang lebih baik)."
                                : message;

                    setError(errorMessage);
                    return { success: false, message: errorMessage };
                }
            }
        } finally {
            setSubmitting(false);
        }
    }, [userId, userName, currentGuru, authUser, getAcademicInfo]);

    return { submitting, error, submitAttendance };
};
